import {
  COPILOT_VIEWS,
  DIALOGS_LAYOUTS,
  SIDEBAR_PANELS,
  TABLE_SIDE_PANELS,
  DialogsSettings,
  SamplesSettings,
  SidebarSettings,
  TableSettings,
  UiSettings,
} from '../types';

/** Проверка значения одного поля сохранённого набора на соответствие текущему составу настроек. */
type FieldGuard = (value: unknown) => boolean;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isString: FieldGuard = (value) => typeof value === 'string';

const isStringArray: FieldGuard = (value) => Array.isArray(value) && value.every(isString);

const isObjectArray: FieldGuard = (value) => Array.isArray(value) && value.every(isPlainObject);

const isFiniteNumber: FieldGuard = (value) => typeof value === 'number' && Number.isFinite(value);

const isOneOf =
  (allowed: readonly string[]): FieldGuard =>
  (value) =>
    typeof value === 'string' && allowed.includes(value);

/**
 * Оставляет в секции только те поля, значения которых система понимает. Поле, не прошедшее
 * проверку, отбрасывается — вместо него применится значение по умолчанию. Поля, для которых
 * правила нет, остаются нетронутыми: их мог записать другой релиз приложения, и терять их
 * при обратном сохранении набора нельзя. Секция не объект — секция берётся по умолчанию.
 */
const dropInvalidFields = <T>(section: unknown, guards: Record<string, FieldGuard>): T | undefined => {
  if (!isPlainObject(section)) return undefined;
  const result = { ...section };
  for (const [key, isValid] of Object.entries(guards)) {
    if (key in result && !isValid(result[key])) delete result[key];
  }
  return result as T;
};

const DIALOGS_PAGE_GUARDS: Record<string, FieldGuard> = {
  layout: isOneOf(DIALOGS_LAYOUTS),
  attributes: isStringArray,
  mainColumns: isStringArray,
  expertReport: isObjectArray,
  hiddenDataTabs: isStringArray,
  lastDataTab: isString,
  attrCollapsedNodes: isStringArray,
  attrTopLevelOrder: isStringArray,
  attrNavCollapsed: (value) => typeof value === 'boolean',
  attrNavWidth: isFiniteNumber,
};

const SAMPLES_PAGE_GUARDS: Record<string, FieldGuard> = {
  visibleColumns: isStringArray,
};

const SIDEBAR_GUARDS: Record<string, FieldGuard> = {
  activePanel: isOneOf(SIDEBAR_PANELS),
  copilotView: isOneOf(COPILOT_VIEWS),
  copilotActiveChatId: (value) => value === null || isFiniteNumber(value),
  copilotWidth: isFiniteNumber,
};

const TABLE_GUARDS: Record<string, FieldGuard> = {
  filters: isObjectArray,
  sorting: isObjectArray,
  offset: isFiniteNumber,
  search: isPlainObject,
  contentSearch: (value) => value === null || isPlainObject(value),
  columnOrder: isStringArray,
  columnVisibility: isPlainObject,
  columnPinning: isPlainObject,
  columnsPanelWidth: isFiniteNumber,
  columnsPanelExpandedGroups: isStringArray,
  activeSidePanel: isOneOf(TABLE_SIDE_PANELS),
  sidePanelPinned: (value) => typeof value === 'boolean',
  scrollIndex: isFiniteNumber,
  scrollKey: isString,
  dismissedSelectionKey: isString,
  selectionDescription: isPlainObject,
  name: isString,
  selectionSource: isString,
  activePresetId: isString,
  pinnedToMenu: (value) => typeof value === 'boolean',
  previousView: isPlainObject,
  previousPresetId: isString,
};

/** Записи таблиц, значение которых не является набором настроек, к применению не берутся. */
const normalizeTables = (tables: unknown): Record<string, TableSettings> | undefined => {
  if (!isPlainObject(tables)) return undefined;
  const result: Record<string, TableSettings> = {};
  for (const [name, value] of Object.entries(tables)) {
    const table = dropInvalidFields<TableSettings>(value, TABLE_GUARDS);
    if (table) result[name] = table;
  }
  return result;
};

/**
 * Приводит набор настроек из профиля к текущему составу настроек приложения.
 *
 * Набор копится у пользователя годами и мог быть записан релизом с другим составом настроек:
 * значение, которого система уже не понимает (устаревший формат, отсутствующая возможность),
 * отбрасывается и применяется по умолчанию, а остальные сохранённые настройки применяются как
 * сохранены. Без этого один непонятный элемент набора роняет экран у давнего пользователя.
 */
export const normalizeUiSettings = (settings: UiSettings): UiSettings => ({
  ...settings,
  dialogsPage: dropInvalidFields<Partial<DialogsSettings>>(settings.dialogsPage, DIALOGS_PAGE_GUARDS),
  samplesPage: dropInvalidFields<Partial<SamplesSettings>>(settings.samplesPage, SAMPLES_PAGE_GUARDS),
  sidebar: dropInvalidFields<SidebarSettings>(settings.sidebar, SIDEBAR_GUARDS),
  tables: normalizeTables(settings.tables),
  dashboardQuality: isPlainObject(settings.dashboardQuality) ? settings.dashboardQuality : undefined,
});
